<script setup lang="ts">
import CopilotPersonalitySelectionCard from './CopilotPersonalities/CopilotPersonalitySelectionCard/CopilotPersonalitySelectionCard.vue';
import FormSection from './FormSection.vue';
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import CopilotPersonalityList from './CopilotPersonalities/CopilotPersonalityList/CopilotPersonalityList.vue';
import CopilotConfigurationTemplateService from '@/core/copilot-configuration-template/copilot-configuration-template.service';
import MeService from '@/core/shared/me/me.service';
import {
  isTeamOwnedTemplate,
  isTemplateOwnedBy,
} from '@/core/copilot-configuration-template/utils/template-ownership.util';
import CopilotActivationService from '@/core/jobs/copilot-activation/copilot-activation.service';
import ProjectCopilotConfigurationUtils from '@/utils/project/project-copilot-configuration.utils';
import { getNameFor } from '@/core/sourcing/utils/userProfile';
import type { PolicyEnforcement, UserProfile } from '@factoryfixinc/ats-interfaces';
import type { ScreeningTypeWithSkip } from '@/core/jobs/copilot-activation/types/local-screener-question.type';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';

const ANIMATION_INTERVAL_MS = 600;

const props = defineProps<{
  isDisabled: boolean;
  isContinueLogicEnabled: boolean;
  isBlurred?: boolean;
  candidateAiDisclosurePolicy?: PolicyEnforcement;
}>();

const emit = defineEmits<{
  (e: 'click:continue'): void;
  (e: 'continue:with-animation'): void;
}>();

const isMenuOpen = ref(false);

const copilotConfigurationTemplateService = new CopilotConfigurationTemplateService();
const copilotActivationService = new CopilotActivationService();
const meService = new MeService();

const currentTextIndex = ref(0);
const animationInterval = ref<NodeJS.Timeout | null>(null);
const showDynamicTexts = ref(false);
const isAnimationComplete = ref(false);

const templates = computed(() => copilotConfigurationTemplateService.templates);
const currentUserId = computed(() => meService.userProfile?.id);
const copilotConfigurationTemplateId = computed(
  () => copilotActivationService.copilotConfigurationTemplateId ?? undefined,
);
const copilotConfigurationTemplate = computed(() =>
  templates.value.find((template) => template.id === copilotConfigurationTemplateId.value),
);
const displayName = computed(() => copilotConfigurationTemplate.value?.displayName ?? 'Copilot');

const isTeamOwned = computed(
  () =>
    copilotConfigurationTemplate.value !== undefined &&
    isTeamOwnedTemplate(copilotConfigurationTemplate.value),
);

const isCreatedByCurrentUser = computed(
  () =>
    copilotConfigurationTemplate.value !== undefined &&
    isTemplateOwnedBy(copilotConfigurationTemplate.value, currentUserId.value),
);

const ownerName = computed(() => {
  if (!copilotConfigurationTemplate.value) {
    return 'FactoryFix';
  }

  if (isTeamOwned.value) {
    return 'your team';
  }

  if (isCreatedByCurrentUser.value) {
    return getNameFor(meService.userProfile as UserProfile);
  }

  const ownerId = copilotConfigurationTemplate.value.ownerId;
  const ownerUser = meService.employerUsers?.find((user) => user.id === ownerId);

  if (!ownerUser) {
    return 'Unknown';
  }

  return getNameFor(ownerUser);
});

const jobTitle = computed(() => copilotActivationService.displayTitle || 'this position');

const isDefaultCopilot = computed(() => copilotConfigurationTemplateId.value == null);
const shouldStopDefaultNavigation = computed(() => !isDefaultCopilot.value);

const shouldShowDynamicTexts = computed(() => showDynamicTexts.value && !isDefaultCopilot.value);

const currentDynamicText = computed(() => {
  if (dynamicTexts.value.length === 0) return '';
  return dynamicTexts.value[currentTextIndex.value];
});

const dynamicTexts = computed(() => [
  `Configuring evaluation criteria for ${jobTitle.value}...`,
  `Setting up sourcing strategy...`,
  `Preparing interview style...`,
  `Ready to start recruiting!`,
]);

async function handleSelectedTemplateId(templateId: number) {
  const previousTemplateId = copilotActivationService.copilotConfigurationTemplateId;
  const isChangingTemplate = previousTemplateId !== undefined && previousTemplateId !== templateId;
  const isCreatingNewJob = props.isContinueLogicEnabled;

  if (templateId) {
    // Load and apply the selected template
    const template = templates.value.find((template) => template.id === templateId);

    if (template?.snapshot) {
      // Apply the template's snapshot to the copilot activation service
      // Parse the snapshot string and ensure all required fields have defaults
      const filledSnapshot = ProjectCopilotConfigurationUtils.parseCopilotConfigurationSnapshot(
        template.snapshot,
        template.version || 'v1',
      );
      copilotActivationService.projectCopilotConfiguration = filledSnapshot;
      copilotActivationService.copilotConfigurationTemplateId = templateId;

      if (typeof filledSnapshot.activeOutreachEnabled === 'boolean') {
        copilotActivationService.activeOutreachEnabled = filledSnapshot.activeOutreachEnabled;
      }

      if (typeof filledSnapshot.scheduledUpdates === 'boolean') {
        copilotActivationService.scheduledUpdates = filledSnapshot.scheduledUpdates;
      }

      if (filledSnapshot.screeningType) {
        const screeningType = filledSnapshot.screeningType as ScreeningTypeWithSkip;
        copilotActivationService.screeningType = screeningType ?? null;
      }
    }
  } else {
    // Reset to default template with proper default values
    // Using empty string will trigger the utility to return defaults
    const defaultSnapshot = ProjectCopilotConfigurationUtils.parseCopilotConfigurationSnapshot(
      '',
      'v1',
    );
    copilotActivationService.projectCopilotConfiguration = defaultSnapshot;
    copilotActivationService.copilotConfigurationTemplateId = undefined;
    copilotActivationService.activeOutreachEnabled = true;
    copilotActivationService.scheduledUpdates = false;
    copilotActivationService.screeningType = null;
  }

  copilotActivationService.enforceEmployerPolicy();

  if (isChangingTemplate && !isCreatingNewJob) {
    SnackbarService.success(
      "You're assigning a different AI recruiter to this job. " +
        'Review settings to ensure they align with your recruitment requirements.',
    );
  }
}

async function handleTemplateCreated(templateId: number): Promise<void> {
  await copilotConfigurationTemplateService.fetchTemplates();
  await handleSelectedTemplateId(templateId);
}

async function startDynamicTextAnimation() {
  showDynamicTexts.value = true;
  currentTextIndex.value = 0;

  await nextTick();

  animationInterval.value = setInterval(() => {
    if (currentTextIndex.value < dynamicTexts.value.length - 1) {
      currentTextIndex.value++;
    } else {
      // Animation complete
      if (animationInterval.value) {
        clearInterval(animationInterval.value);
        animationInterval.value = null;
      }
      isAnimationComplete.value = true;
      emit('continue:with-animation');
    }
  }, ANIMATION_INTERVAL_MS);
}

function handleContinueClick() {
  if (isDefaultCopilot.value) {
    // Default copilot - emit normal continue event
    emit('click:continue');
  } else {
    // Non-default copilot - start animation
    startDynamicTextAnimation();
  }
}

onMounted(async () => {
  await Promise.all([
    copilotConfigurationTemplateService.fetchTemplates(),
    meService.fetchEmployerUsers(),
  ]);
});

onBeforeUnmount(() => {
  // Clear animation interval if exists
  if (animationInterval.value) {
    clearInterval(animationInterval.value);
    animationInterval.value = null;
  }
  // Reset animation state
  currentTextIndex.value = 0;
  showDynamicTexts.value = false;
  isAnimationComplete.value = false;
});
</script>

<template>
  <FormSection
    id="copilot-configuration-template"
    title="Select your AI recruiter"
    subtitle="Choose the AI recruiter that best fits this role"
    next-section-id="must-have-skills"
    :is-disabled="isDisabled"
    :is-blurred="isBlurred"
    :is-continue-logic-enabled="isContinueLogicEnabled"
    :should-stop-navigation="shouldStopDefaultNavigation"
    @click:continue="handleContinueClick"
  >
    <CopilotPersonalityList
      v-model="isMenuOpen"
      :candidate-ai-disclosure-policy="candidateAiDisclosurePolicy"
      :templates="templates"
      :owner-id="currentUserId"
      :selected-template-id="copilotConfigurationTemplateId"
      :employer-users="meService.employerUsers"
      @select="handleSelectedTemplateId"
      @create:template="handleTemplateCreated"
    >
      <template #activator="{ props }">
        <CopilotPersonalitySelectionCard
          v-bind="props"
          :display-name="displayName"
          :owner-name="ownerName"
          :is-created-by-current-user="isCreatedByCurrentUser"
          :is-team-owned="isTeamOwned"
          :is-default="!copilotConfigurationTemplateId"
        />
      </template>
    </CopilotPersonalityList>
    <transition name="fade" mode="out-in">
      <div
        v-if="shouldShowDynamicTexts"
        class="mt-4 flex h-14 items-center gap-2 rounded-lg bg-copilot-0 p-4"
      >
        <div class="h-2 w-2 animate-pulse rounded-full bg-[#A855F7]"></div>
        <span class="text-sm font-bold text-[#581C87] text-copilot">{{ currentDynamicText }}</span>
      </div>
    </transition>
  </FormSection>
</template>
